import { useState, FormEvent, FC } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Sparkles,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Users,
  ShieldCheck,
  Link2,
  Zap,
  MessageSquare,
} from 'lucide-react';

const commercialHighlights = [
  {
    icon: Users,
    title: 'Gestão Inteligente de Grupos',
    description:
      'Sincronização em tempo real de membros e transbordo automático de vagas sem intervenção manual.',
  },
  {
    icon: ShieldCheck,
    title: 'Esteira de Disparo com Jitter',
    description:
      'Cadência estocástica calibrada para proteção de chips e máxima segurança da linha.',
  },
  {
    icon: Link2,
    title: 'Links Rastreados & SubIDs Canônicos',
    description:
      'Rastreabilidade total da origem de cada clique e comissão em todos os canais e projetos.',
  },
  {
    icon: Zap,
    title: 'Páginas Ponte de Alta Conversão',
    description:
      'Direcionamento instantâneo de tráfego com gatilhos visuais de escassez e cronômetro.',
  },
];

export const LoginForm: FC = () => {
  const { login } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const validate = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    if (!email.trim()) {
      errors.email = 'Informe seu e-mail de acesso.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Formato de e-mail inválido.';
    }

    if (!password) {
      errors.password = 'Informe sua senha.';
    } else if (password.length < 6) {
      errors.password = 'A senha deve ter pelo menos 6 caracteres.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await login(email.trim(), password);
      toastSuccess('Login efetuado com sucesso.', 'Bem-vinda ao TrafficForge!');
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Credenciais inválidas ou serviço indisponível.';
      toastError(msg, 'Falha no Login');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full overflow-hidden bg-slate-950 text-slate-100">
      {/* Efeitos visuais de iluminação no fundo */}
      <div className="pointer-events-none absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-teal-500/10 blur-3xl" />

      {/* Lado Esquerdo: Destaques Comerciais (Exibido apenas em telas grandes) */}
      <div className="backdrop-blur-xs relative z-10 hidden flex-col justify-between border-r border-slate-900 bg-slate-950/60 p-12 lg:flex lg:w-1/2 xl:w-7/12 xl:p-16">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Automação &amp; Escala para Afiliados</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white xl:text-4xl">
            TrafficForge
          </h1>
          <p className="max-w-lg text-xs leading-relaxed text-slate-400 xl:text-sm">
            Centralize sua operação de vendas, esteira de WhatsApp e captação de
            leads com automação profissional de ponta a ponta.
          </p>
        </div>

        {/* Lista de Recursos Comerciais */}
        <div className="my-10 max-w-lg space-y-6">
          {commercialHighlights.map((feature) => {
            const Icon = feature.icon;
            return (
              <div key={feature.title} className="flex items-start gap-4">
                <div className="shadow-xs mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-emerald-400">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-white">
                    {feature.title}
                  </h3>
                  <p className="text-xs leading-relaxed text-slate-400">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t border-slate-900 pt-6 text-xs text-slate-500">
          <span>Acesso exclusivo para parceiros autorizados</span>
          <span>v0.1.0</span>
        </div>
      </div>

      {/* Lado Direito: Caixa de Login */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center p-6 sm:p-10 lg:w-1/2 xl:w-5/12">
        {/* Identidade em telas menores */}
        <div className="mb-8 text-center lg:hidden">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-400 font-black text-slate-950 shadow-xl shadow-emerald-500/20">
            <Sparkles className="h-6 w-6 text-slate-950" />
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white">
            TrafficForge
          </h2>
          <p className="mt-0.5 text-xs text-slate-400">
            Automação Inteligente para Afiliados
          </p>
        </div>

        <div className="w-full max-w-md space-y-6">
          {/* Card do Formulário */}
          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/80 p-6 shadow-2xl shadow-black/80 backdrop-blur-xl sm:p-8">
            <div className="mb-6 border-b border-slate-800 pb-4">
              <h2 className="text-base font-bold text-white">Acessar Painel</h2>
              <p className="mt-0.5 text-xs text-slate-400">
                Entre com suas credenciais para gerenciar sua operação
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <Input
                id="email-input"
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email)
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="seu.email@exemplo.com"
                autoComplete="email"
                leftIcon={<Mail className="h-4 w-4" />}
                error={fieldErrors.email}
              />

              <Input
                id="password-input"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password)
                    setFieldErrors((prev) => ({
                      ...prev,
                      password: undefined,
                    }));
                }}
                placeholder="••••••••"
                autoComplete="current-password"
                leftIcon={<Lock className="h-4 w-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="p-1 text-content-muted transition-colors hover:text-content-main"
                    aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                error={fieldErrors.password}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isSubmitting}
                rightIcon={<ArrowRight className="h-4 w-4" />}
                className="mt-2 w-full"
              >
                {isSubmitting ? 'Validando credenciais...' : 'Entrar no Painel'}
              </Button>
            </form>

            {/* Canal de Contato WhatsApp para Solicitação de Acesso */}
            <div className="mt-6 space-y-1.5 border-t border-slate-800 pt-5 text-center">
              <p className="text-xs text-slate-400">
                Não possui uma conta de acesso?
              </p>
              <a
                href="https://wa.me/553182973810?text=Ol%C3%A1!%20Gostaria%20de%20solicitar%20meu%20acesso%20ao%20TrafficForge."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg p-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/10 hover:text-emerald-300"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                <span>Fale conosco no WhatsApp (+55 31 8297-3810)</span>
              </a>
            </div>
          </div>

          {/* Rodapé Limpo */}
          <p className="text-center text-micro text-slate-600">
            TrafficForge © {new Date().getFullYear()} • Todos os direitos
            reservados
          </p>
        </div>
      </div>
    </div>
  );
};
